import React, { useState } from 'react';
import { Collapse } from 'react-bootstrap';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import useLanguage from '../hooks/useLanguage';
import { formatCount } from '../utils/numberFormatter';
import { supplyContents } from '../config/suppliesLabels';

/**
 * Read-only checklist + supplies results for ops/supervisor review.
 *
 * No interaction — ops looks at what the crew recorded, they don't change it.
 * Answers are the crew's certification of the unit; the review surface must not
 * let ops silently overwrite them.
 *
 * Colours mirror the crew sheet exactly (green positive · red negative ·
 * orange needs-attention · grey N/A or unanswered) so the two never diverge.
 */

const STATUS_STYLE = {
  done: { key: 'tasks.checklist.done', color: '#48b58d' },
  not_done: { key: 'tasks.checklist.notDone', color: '#DC3545' },
  na: { key: 'tasks.checklist.na', color: '#6c757d' },
  ok: { key: 'tasks.checklist.available', color: '#48b58d' },
  needs_restock: { key: 'tasks.checklist.needsRestock', color: '#ff9f53' }
};

const StatusPill = ({ status }) => {
  const { t } = useLanguage();
  const style = STATUS_STYLE[status];
  const label = style ? t(style.key) : t('tasks.checklist.unanswered');
  const color = style ? style.color : '#adb5bd';
  return (
    <span
      className="fw-semibold"
      style={{
        fontSize: '0.8rem',
        color,
        border: `1.5px solid ${color}`,
        borderRadius: '12px',
        padding: '2px 10px',
        whiteSpace: 'nowrap'
      }}
    >
      {label}
    </span>
  );
};

const Row = ({ label, status, note, contents }) => {
  return (
    <div className="d-flex justify-content-between align-items-center gap-2 py-1">
      <div style={{ fontSize: '0.9rem', color: '#090040' }}>
        {label}
        {/* What the item contains — informational, so it carries no pill of its own */}
        {contents && <div className="text-muted" style={{ fontSize: '0.8rem' }}>{contents}</div>}
        {note && <div className="small text-muted">{note}</div>}
      </div>
      <StatusPill status={status} />
    </div>
  );
};

// Collapsible results block — header shows a one-line summary so ops can scan
// without expanding; the modal stays short (status/assignee/notes above the fold).
const Section = ({ title, summary, defaultOpen = false, language, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded mb-3" style={{ background: '#f8f6fc', border: '1px solid #e5ddf2' }}>
      <button
        type="button"
        className="btn w-100 d-flex justify-content-between align-items-center p-3 border-0 bg-transparent"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="fw-semibold" style={{ color: '#471396' }}>{title}</span>
        <span className="d-flex align-items-center gap-2">
          {summary}
          {open ? <FaChevronUp size={12} color="#471396" /> : <FaChevronDown size={12} color="#471396" />}
        </span>
      </button>
      <Collapse in={open}>
        <div><div className="px-3 pb-3">{children}</div></div>
      </Collapse>
    </div>
  );
};

const CleaningResultsSummary = ({ task }) => {
  const { t, isArabic, language } = useLanguage();

  const checklist = task?.checklist || [];
  const supplies = task?.supplies || [];
  const restockItems = task?.restockItems || [];
  if (checklist.length === 0 && supplies.length === 0 && restockItems.length === 0) return null;

  const label = (item) => (isArabic ? item.labelAr : (item.labelEn || item.labelAr));

  // Small coloured count badge for a header summary (e.g. "٣ لم يتم")
  const flag = (n, color, text) => n > 0 && (
    <span className="fw-semibold" style={{ fontSize: '0.78rem', color, border: `1.5px solid ${color}`, borderRadius: '12px', padding: '1px 8px' }}>
      {formatCount(n, language)} {text}
    </span>
  );

  const checklistNotDone = checklist.filter(i => i.status === 'not_done').length;
  const checklistUnanswered = checklist.filter(i => !i.status).length;
  const suppliesFlagged = supplies.filter(i => i.status === 'needs_restock').length;
  const suppliesUnanswered = supplies.filter(i => !i.status).length;

  // Group checklist by its (already ordered) section
  const groups = [];
  checklist.forEach(item => {
    const key = item.section || '';
    const last = groups[groups.length - 1];
    if (last && last.sectionKey === key) last.items.push(item);
    else groups.push({
      sectionKey: key,
      title: isArabic ? item.section : (item.sectionEn || item.section),
      items: [item]
    });
  });

  return (
    <div dir={isArabic ? 'rtl' : 'ltr'}>
      {checklist.length > 0 && (
        <Section
          language={language}
          title={`📋 ${t('tasks.checklist.resultsTitle')}`}
          summary={
            <>
              {flag(checklistNotDone, '#DC3545', t('tasks.checklist.notDone'))}
              {flag(checklistUnanswered, '#adb5bd', t('tasks.checklist.unanswered'))}
            </>
          }
        >
          {groups.map((group, idx) => (
            <div key={group.sectionKey || idx} className="mb-2">
              {group.title && (
                <div className="small fw-bold text-muted mb-1">{group.title}</div>
              )}
              {group.items.map(item => (
                <Row key={item.itemId} label={label(item)} status={item.status} note={item.comment} />
              ))}
            </div>
          ))}
        </Section>
      )}

      {supplies.length > 0 && (
        <Section
          language={language}
          title={`🧴 ${t('tasks.checklist.suppliesResultsTitle')}`}
          summary={
            <>
              {flag(suppliesFlagged, '#ff9f53', t('tasks.checklist.needsRestock'))}
              {flag(suppliesUnanswered, '#adb5bd', t('tasks.checklist.unanswered'))}
            </>
          }
        >
          {supplies.map(item => (
            <Row
              key={item.itemId}
              label={label(item)}
              status={item.status}
              note={item.note}
              contents={supplyContents(t, item.itemId)}
            />
          ))}
        </Section>
      )}

      {/* Restock task — the items the crew needs to refill. Open by default:
          it's short and it IS the point of the task. */}
      {restockItems.length > 0 && (
        <Section
          language={language}
          defaultOpen
          title={`🧴 ${t('tasks.checklist.restockTitle')}`}
          summary={flag(restockItems.length, '#ff9f53', '')}
        >
          <ul className="mb-0 ps-3">
            {restockItems.map(item => (
              <li key={item.itemId} style={{ fontSize: '0.9rem' }}>{label(item)}</li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
};

export default CleaningResultsSummary;
